import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  CaptableAiNotification,
  watchCaptableAIBatch,
  watchCaptableAINotifications,
} from "../../api/companyExcelOnboarding";
import { useAuthStore } from "../../store";
import {
  isTerminalCaptableBatch,
  useBackgroundJobsStore,
} from "../../store/useBackgroundJobsStore";

const reconnectDelay = (milliseconds: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(() => resolve(), milliseconds);
  });

export default function BackgroundJobsManager() {
  const batches = useBackgroundJobsStore((state) => state.batches);
  const batchContexts = useBackgroundJobsStore((state) => state.batchContexts);
  const initialize = useBackgroundJobsStore((state) => state.initialize);
  const initialized = useBackgroundJobsStore((state) => state.initialized);
  const accessToken = useAuthStore((state) => state.accessToken);
  const activeBatchIds = Object.keys(batches)
    .filter(
      (id) =>
        !isTerminalCaptableBatch(batches[id]) &&
        batchContexts[id]?.flow !== "company-onboarding"
    )
    .sort()
    .join(",");

  useEffect(() => {
    if (accessToken) initialize();
  }, [accessToken, initialize]);

  useEffect(() => {
    if (!accessToken || !initialized) return undefined;
    const controller = new AbortController();
    const run = async () => {
      const store = useBackgroundJobsStore.getState();
      let attempt = 0;
      /* eslint-disable no-await-in-loop */
      while (!controller.signal.aborted) {
        try {
          if (attempt > 0) await store.fetchNotifications(true);
          if (controller.signal.aborted) return;
          await watchCaptableAINotifications(
            (notification: CaptableAiNotification) => {
              const isNew = store.applyNotification(notification);
              if (!isNew) return;
              const status = (
                notification.workflowStatus || notification.status
              ).toUpperCase();
              if (status === "COMPLETED") toast.success(notification.message);
              else if (
                status === "COMPLETED_WITH_ERRORS" ||
                status === "ACTION_REQUIRED" ||
                status === "NEEDS_INPUT" ||
                status === "READY_TO_ONBOARD"
              )
                toast.warning(notification.message);
              else if (status === "FAILED") toast.error(notification.message);
              else toast.info(notification.message);
            },
            controller.signal,
            () => store.setNotificationStreamConnected(true)
          );
        } catch (_) {
          if (controller.signal.aborted) return;
        }
        store.setNotificationStreamConnected(false);
        attempt += 1;
        await reconnectDelay(Math.min(1000 * 2 ** attempt, 30000));
      }
      /* eslint-enable no-await-in-loop */
    };
    run();
    return () => {
      controller.abort();
      useBackgroundJobsStore.getState().setNotificationStreamConnected(false);
    };
  }, [accessToken, initialized]);

  useEffect(() => {
    if (!accessToken) return undefined;
    const controllers: AbortController[] = [];
    Object.values(batches).forEach((batch) => {
      if (isTerminalCaptableBatch(batch)) return;
      if (batchContexts[batch.batchId]?.flow === "company-onboarding") return;
      const controller = new AbortController();
      controllers.push(controller);
      const run = async () => {
        const store = useBackgroundJobsStore.getState();
        let attempt = 0;
        /* eslint-disable no-await-in-loop */
        while (!controller.signal.aborted) {
          store.setConnection(
            batch.batchId,
            attempt ? "reconnecting" : "loading"
          );
          try {
            // Status is fetched before every (re)subscription so missed events never
            // become the source of truth.
            const current = await store.refreshBatch(batch.batchId);
            if (controller.signal.aborted || isTerminalCaptableBatch(current))
              return;
            if (attempt) store.setConnection(batch.batchId, "recovered");
            await watchCaptableAIBatch(
              batch.batchId,
              sessionStorage.getItem("accesstoken") || "",
              (update) => {
                store.applyBatch(update);
                store.setConnection(batch.batchId, "connected");
              },
              controller.signal
            );
            if (controller.signal.aborted) return;
            const latest = await store.refreshBatch(batch.batchId);
            if (isTerminalCaptableBatch(latest)) return;
          } catch (_) {
            if (controller.signal.aborted) return;
            store.setConnection(batch.batchId, "failed");
          }
          attempt += 1;
          await reconnectDelay(Math.min(1000 * 2 ** attempt, 30000));
        }
        /* eslint-enable no-await-in-loop */
      };
      run();
    });
    return () => controllers.forEach((controller) => controller.abort());
  }, [activeBatchIds, batchContexts, accessToken]);

  return null;
}
